import { config } from "../config.js";
import jwtDecode from "jwt-decode";

function isFunc(variable) {
  return typeof variable === "function";
}

// using https://github.com/aws-amplify/amplify-js/tree/master/packages/amazon-cognito-identity-js
class Authentication {
  constructor() {
    // userPoolId and clientId alone are not enough to do any
    // malicious activity on your user pool
    this.poolData = {
      UserPoolId: config.userPoolId, // Your user pool id here
      ClientId: config.clientId, // Your client id here
      Storage: new AmazonCognitoIdentity.CookieStorage({
        domain: config.domain,
        secure: config.secure,
      }),
    };
    this.userPool = new AmazonCognitoIdentity.CognitoUserPool(this.poolData);
  }

  async verifyAndAuthenticate(verificationCode, callback) {
    var user = document.getElementById("email").value;
    var pw = document.getElementById("password").value;

    var authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(
      {
        Username: user,
        Password: pw,
      }
    );

    var cognitoUser = this.getUser(user);

    // try and log in to see if user is confirmed
    await cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: function (result) {
        // user already is confirmed
        if (isFunc(callback)) callback(true);
      },

      onFailure: async function (err) {
        var message = err.message || JSON.stringify(err);
        if (err.code == "UserNotConfirmedException") {
          return;
        } else if (isFunc(callback)) callback(false, message);
      },
    });

    // if the user is not logged in yet, verify
    await cognitoUser.confirmRegistration(
      verificationCode,
      true,
      function (err, res) {
        if (
          err &&
          err.message != "User cannot be confirmed. Current status is CONFIRMED"
        ) {
          var message = err.message || JSON.stringify(err);
          if (isFunc(callback)) {
            callback(false, message);
            return;
          }
        }

        // verification success, authenticate user
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: function (result) {
            if (isFunc(callback)) callback(true);
          },

          onFailure: function (err) {
            var message = err.message || JSON.stringify(err);
            if (isFunc(callback)) callback(false, message);
          },
        });
      }
    );
  }

  authenticate(callback) {
    var user = document.getElementById("email").value;
    var pw = document.getElementById("password").value;
    var authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(
      {
        Username: user,
        Password: pw,
      }
    );

    var cognitoUser = this.getUser(user);
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: function (result) {
        if (isFunc(callback)) callback(true);
      },

      onFailure: function (err) {
        var message = err.message || JSON.stringify(err);
        if (isFunc(callback)) callback(false, message);
      },
    });
  }

  getUser(username) {
    return new AmazonCognitoIdentity.CognitoUser({
      Username: username,
      Pool: this.userPool,
      Storage: new AmazonCognitoIdentity.CookieStorage({
        domain: config.domain,
        secure: config.secure,
      }),
    });
  }

  getAuthenticatedUser() {
    var cognitoUser = this.userPool.getCurrentUser();
    if (!cognitoUser) {
      return null;
    }

    return cognitoUser.username;
  }

  // Updates the given cognito user attribute for the current user
  updateUserDetails(attrName, attrValue, callback) {
    var cognitoUser = this.userPool.getCurrentUser();

    // Let's just do one at a time for now
    var attributeList = [
      new AmazonCognitoIdentity.CognitoUserAttribute({
        Name: attrName,
        Value: attrValue,
      }),
    ];

    // NOTE: getSession must be called to authenticate
    // user before updating their attributes
    cognitoUser.getSession((err, result) => {
      if (err) {
        console.log(err.message || JSON.stringify(err));
        if (isFunc(callback)) callback(false);
      }

      // Attempt to save attribute to cognito user
      cognitoUser.updateAttributes(attributeList, (err, result) => {
        this.sendResult(callback, err);
      });
    });
  }

  // Fetches all cognito user attributes for the current user.
  async asyncGetUserDetails(callback) {
    // Fetch session ata
    var cognitoUser = this.userPool.getCurrentUser();
    if (!cognitoUser) {
      return;
    }

    return new Promise(function (resolve, reject) {
      let attributes = [];
      // NOTE: getSession must be called to authenticate
      // user before calling getUserAttributes
      cognitoUser.getSession(function (err, session) {
        if (err) {
          console.log(err.message || JSON.stringify(err));
          return reject;
        }

        // Fetch attributes related to the current user
        cognitoUser.getUserAttributes(function (err, result) {
          if (err) {
            console.log(err.message || JSON.stringify(err));
            return reject;
          }

          // Building the attribute list
          for (let i = 0; i < result.length; i++) {
            attributes.push({
              name: result[i].getName(),
              value: result[i].getValue(),
            });
          }

          // Since the cognito functions are asyncronous, we need to
          // use callbacks to get the data back.
          if (isFunc(callback)) resolve(callback(attributes));
        });
      });
    });
  }

  // Fetches all cognito user attributes for the current user.
  getUserDetails(callback) {
    let attributes = [];

    // Fetch session data
    var cognitoUser = this.userPool.getCurrentUser();
    if (!cognitoUser) {
      return;
    }

    // NOTE: getSession must be called to authenticate
    // user before calling getUserAttributes
    cognitoUser.getSession(function (err, session) {
      if (err) {
        console.log(err.message || JSON.stringify(err));
        return;
      }

      // Fetch attributes related to the current user
      cognitoUser.getUserAttributes(function (err, result) {
        if (err) {
          console.log(err.message || JSON.stringify(err));
          return;
        }

        // Building the attribute list
        for (let i = 0; i < result.length; i++) {
          attributes.push({
            name: result[i].getName(),
            value: result[i].getValue(),
          });
        }

        // Since the cognito functions are asyncronous, we need to
        // use callbacks to get the data back.
        if (isFunc(callback)) callback(attributes);
      });
    });
  }

  async getUserGroups(callback) {
    // Fetch session data
    var cognitoUser = this.userPool.getCurrentUser();
    if (!cognitoUser) {
      return;
    }
    return new Promise(function (resolve, reject) {
      let groups = [];

      // NOTE: getSession must be called to authenticate
      // user before calling getUserAttributes
      cognitoUser.getSession(function (err, session) {
        if (err) {
          console.log(err.message || JSON.stringify(err));
          return reject;
        }
        var sessionIdInfo = jwtDecode(session.getIdToken().jwtToken);
        groups = sessionIdInfo["cognito:groups"];
        // Since the cognito functions are asyncronous, we need to
        // use callbacks to get the data back.
        if (isFunc(callback)) resolve(callback(groups));
      });
    });
  }

  // retrieves the idToken
  async getIdToken() {
    // Fetch session data
    var cognitoUser = this.userPool.getCurrentUser();
    if (!cognitoUser) {
      return;
    }
    return new Promise(function (resolve, reject) {
      // NOTE: getSession must be called to authenticate
      // user before calling getUserAttributes
      cognitoUser.getSession(function (err, session) {
        if (err) {
          console.error(err.message || JSON.stringify(err));
          return reject;
        } else if (!session.isValid()) {
          console.error("invalid session");
          return reject;
        } else {
          let token = session.getIdToken();
          if (token)
            return resolve({
              idToken: token.jwtToken,
              user: cognitoUser.username,
            });
        }
      });
    });
  }

  resendVerification(callback) {
    var username = document.getElementById("email").value;
    var cognitoUser = this.getUser(username);

    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) {
        var message = err.message || JSON.stringify(err);
        if (isFunc(callback)) callback(false, message);
        return;
      }

      if (isFunc(callback)) callback(true);
    });
  }

  // Starts the forgot password process
  sendResetCode(callback) {
    var username = document.getElementById("email").value;
    var cognitoUser = this.getUser(username);

    cognitoUser.forgotPassword({
      // Password reset process has started
      onSuccess: function (data) {},

      // Something went wrong
      onFailure: function (err) {
        var message = err.message || JSON.stringify(err);
        if (isFunc(callback)) callback(false, message);
      },

      // User has a verification code to use
      inputVerificationCode: function (err) {
        if (isFunc(callback)) callback(true);
      },
    });
  }

  // Reset your password with reset code
  resetPass(username, callback) {
    var code = document.getElementById("code").value;
    var newPassword = document.getElementById("newPassword").value;
    var cognitoUser = this.getUser(username);

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess() {
        if (isFunc(callback)) callback(true);
      },
      onFailure(err) {
        var message = err.message || JSON.stringify(err);
        if (isFunc(callback)) callback(false, message);
      },
    });
  }

  changePass(oldPass, newPass, callback) {
    var cognitoUser = this.userPool.getCurrentUser();
    cognitoUser.getSession((err, result) => {
      if (err) {
        console.log(err.message || JSON.stringify(err));
        if (isFunc(callback)) callback(false);
      }

      cognitoUser.changePassword(oldPass, newPass, (err, result) => {
        this.sendResult(callback, err);
      });
    });
  }

  // Logs the user out
  async logout() {
    var cognitoUser = this.userPool.getCurrentUser();
    if (cognitoUser) {
      await cognitoUser.signOut();
    }
  }

  registerUser(user, pass, fname, lname, callback) {
    let attrs = [];
    attrs.push(
      new AmazonCognitoIdentity.CognitoUserAttribute({
        Name: "email",
        Value: user,
      })
    );
    attrs.push(
      new AmazonCognitoIdentity.CognitoUserAttribute({
        Name: "name",
        Value: fname,
      })
    );
    attrs.push(
      new AmazonCognitoIdentity.CognitoUserAttribute({
        Name: "family_name",
        Value: lname,
      })
    );
    this.userPool.signUp(user, pass, attrs, null, (err, result) => {
      this.sendResult(callback, err);
    });
  }

  deleteUser(callback) {
    var cognitoUser = this.userPool.getCurrentUser();
    cognitoUser.deleteUser((err, result) => {
      this.sendResult(callback, err);
    });
  }

  sendResult(callback, err) {
    if (isFunc(callback)) {
      if (err) {
        var message = err.message || JSON.stringify(err);
        callback(false, message);
        return;
      }

      callback(true);
    }
  }
}

export let auth = new Authentication();
